import { useState } from 'react'
import { useSendListingMessage } from './listings/useSendListingMessage'
import { useTrackingContext } from '../providers/tracking'
import { useSelector } from 'react-redux'
import { selectCustomerIsRealEstateAgent, selectCustomerType } from '../store/selectors/customerProfile'
import { Listing, MessageTopicKey } from '../api/gqlaxy/gql/generated/graphql'
import { TrackingEventConstants } from '../constants/tracking'
import { TrackingEventHelpers } from '../helpers/tracking'
import {
  selectScheduleTourDate,
  selectScheduleTourIsInPerson,
  selectScheduleTourPhone,
  selectScheduleTourPreferredAgent
} from '../store/slices/scheduleTour'
import { selectClientFirstName } from '../store/selectors/client'
import * as Sentry from '@sentry/browser'
import { selectPreferredRealEstateAgentMembership } from '../store/selectors/clientTeamMemberships'

export const useScheduleTourStep = ({ listing }: { listing: Listing }) => {
  const [isLoading, setIsLoading] = useState(false)
  const { trackingClient } = useTrackingContext()
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const { mutateAsync: sendListingMessage } = useSendListingMessage()
  const tourDate = useSelector(selectScheduleTourDate)
  const tourPhone = useSelector(selectScheduleTourPhone)
  const inPerson = useSelector(selectScheduleTourIsInPerson)
  const clientFirstName = useSelector(selectClientFirstName)
  const customerType = useSelector(selectCustomerType)
  const agentPreference = useSelector(selectScheduleTourPreferredAgent)
  const reaMembership = useSelector(selectPreferredRealEstateAgentMembership)

  const scheduleTour = async (message: string) => {
    try {
      setIsLoading(true)

      const topicKey = isCustomerREA
        ? MessageTopicKey.ListingsShowingRequest
        : MessageTopicKey.ListingsShowingRequestToListingAgent

      trackingClient?.questionAsked({ direct_message_topic: topicKey })
      trackingClient?.clientRequestedInfo({
        request_type: TrackingEventConstants.CLIENT_SCHEDULED_SHOWING,
        customer_type: TrackingEventHelpers.getTrackingCustomerType(customerType)!
      })

      await sendListingMessage({
        input: {
          extraMessage: message,
          listingId: listing.id,
          topicKey: topicKey,
          phone: `1-${tourPhone}`,
          message: `${clientFirstName} requests ${
            inPerson ? 'an in person' : 'a virtual'
          } showing on ${tourDate.toDateString()}`,
          ...(tourDate && { scheduleTour: { tourDate: tourDate.toISOString(), inPerson: inPerson } }),
          sendToPreferredAgent: agentPreference === 'preferredAgent' && !!reaMembership
        }
      })
    } catch (e) {
      Sentry.captureException(e)
      throw e
    } finally {
      setIsLoading(false)
    }
  }

  return {
    scheduleTour,
    isLoading
  }
}
